import React, { useCallback, useEffect, useRef, useState } from "react";

import PageLayout from "../../components/PageLayout";
import ActionLoader from "../../components/ActionLoader";
import Toast from "../../components/Toast";

import { GET_HEDGE_LOCK_REPORT_URL, getData } from "../../utils/api";
import { getLoginDetails, canAccessHedgeLockReport } from "../../utils/storage";

import InsightsHeaderIcon from "../../icons/newicons/insights.svg";

import "./insights.css";
import "./converter-testing.css";
import { InsightsSubNav } from "./InsightsSubNav";

const PAGE_SIZE = 10;

function formatDateTime(ms) {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return "—";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
}

function paginationLabel(offset, pageSize, totalCount, pageLength) {
  if (!totalCount) return "No records";
  const from = totalCount === 0 ? 0 : offset + 1;
  const to = offset + pageLength;
  return `Showing ${from}–${to} of ${totalCount.toLocaleString()}`;
}

function parseReportResponse(res) {
  const inner = res?.data != null ? res.data : res;
  return {
    list: inner?.list || [],
    totalCount: inner?.totalCount ?? 0,
    areMoreItemsAvailable: Boolean(inner?.areMoreItemsAvailable),
  };
}

function mapListItems(list) {
  return list.map((item) => ({
    ...item,
    companyName: item.companyName || "—",
    username: item.username || "—",
    name: item.name || "—",
    batchSeq: item.batchSeq != null && item.batchSeq !== "" ? item.batchSeq : "—",
    locktsLabel: formatDateTime(item.lockts),
  }));
}

const HedgeLock = () => {
  const canViewHedgeLock = canAccessHedgeLockReport(getLoginDetails());

  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [areMoreItemsAvailable, setAreMoreItemsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: 0 });

  const fetchTokenRef = useRef(0);

  const fetchReport = useCallback(async (text, pageOffset) => {
    const token = ++fetchTokenRef.current;
    try {
      setIsLoading(true);
      const params = {
        count: PAGE_SIZE,
        offset: pageOffset,
      };
      if (text) params.text = text;

      const res = await getData(GET_HEDGE_LOCK_REPORT_URL, params);
      if (token !== fetchTokenRef.current) return;

      const parsed = parseReportResponse(res);
      setList(parsed.list);
      setTotalCount(parsed.totalCount);
      setAreMoreItemsAvailable(parsed.areMoreItemsAvailable);
    } catch (err) {
      if (token !== fetchTokenRef.current) return;
      console.log("Hedge lock report error", err);
      setList([]);
      setTotalCount(0);
      setAreMoreItemsAvailable(false);
      setToastParams({
        message: "Failed to load hedge lock report.",
        type: "error",
        id: Date.now(),
      });
    } finally {
      if (token === fetchTokenRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!canViewHedgeLock) return;
    fetchReport(appliedSearch, offset);
  }, [appliedSearch, offset, fetchReport, canViewHedgeLock]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedSearch(searchText.trim());
    setOffset(0);
  };

  const handleClearFilters = () => {
    setSearchText("");
    setAppliedSearch("");
    setOffset(0);
  };

  const tableItems = mapListItems(list);

  if (!canViewHedgeLock) {
    return (
      <PageLayout heading="Insights" hideSearchBar icon={InsightsHeaderIcon}>
        <InsightsSubNav />
        <div className="c-gray fs-16">You do not have permission to view the hedge lock report.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout heading="Insights" hideSearchBar icon={InsightsHeaderIcon}>
      <InsightsSubNav />

      <form className="converter-testing-filters" onSubmit={handleApplyFilters}>
        <div className="converter-testing-filter-field converter-testing-filter-search">
          <label htmlFor="hedge-lock-search" className="converter-testing-filter-sr">
            Search
          </label>
          <input
            id="hedge-lock-search"
            type="text"
            value={searchText}
            placeholder="Search company / user / batch..."
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className="converter-testing-filter-actions">
          <button type="submit" className="converter-testing-filter-btn converter-testing-filter-btn-apply">
            Apply
          </button>
          <button
            type="button"
            className="converter-testing-filter-btn converter-testing-filter-btn-clear"
            onClick={handleClearFilters}
          >
            Clear
          </button>
        </div>
      </form>

      {isLoading && <ActionLoader />}

      {!isLoading && (
        <>
          <div className="converter-testing-details-meta">
            {paginationLabel(offset, PAGE_SIZE, totalCount, tableItems.length)}
          </div>
          {tableItems.length === 0 ? (
            <div className="c-gray fs-14">No price-locked batches match your search.</div>
          ) : (
            <>
              <div className="converter-testing-drift-wrap">
                <table className="converter-testing-drift-table converter-testing-hedge-lock-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Username</th>
                      <th>Batch Name</th>
                      <th>Batch Seq</th>
                      <th>Lock Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableItems.map((item, index) => (
                      <tr key={item.batchId || `${item.batchSeq}-${index}`}>
                        <td>{item.companyName}</td>
                        <td>{item.username}</td>
                        <td>{item.name}</td>
                        <td>{item.batchSeq}</td>
                        <td>{item.locktsLabel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="converter-testing-pagination">
                <button
                  type="button"
                  className="converter-testing-page-btn converter-testing-page-btn-prev"
                  disabled={offset === 0}
                  onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="converter-testing-page-btn converter-testing-page-btn-next"
                  disabled={!areMoreItemsAvailable}
                  onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </>
      )}

      <Toast
        message={toastParams.message}
        type={toastParams.type}
        id={toastParams.id}
      />
    </PageLayout>
  );
};

export default HedgeLock;
